import { useState } from 'react';

const API = 'https://tcio-api.onrender.com';

export default function Login({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password: password.trim() }),
      });
      const data = await res.json();
      if (!data.ok) { setError(data.error || 'Login failed'); setLoading(false); return; }
      if (data.must_change_password) {
        setTempUser(data);
        setMustChange(true);
        setLoading(false);
        return;
      }
      onLogin(data);
    } catch (err) {
      setError('Connection error — is the API running?');
    }
    setLoading(false);
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Enter your email above first.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (res.status === 429) { setError(data.error || 'Too many requests.'); setLoading(false); return; }
      setForgotSent(true);
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    const np = newPassword.trim();
    if (np !== confirmPassword.trim()) { setError('Passwords do not match'); return; }
    if (np.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tempUser.token}` },
        body: JSON.stringify({ new_password: np }),
      });
      const data = await res.json();
      if (!data.ok) { setError(data.error || 'Failed to change password'); setLoading(false); return; }
      onLogin({ ...tempUser, must_change_password: false });
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>TCIO</div>
        <div style={s.subtitle}>Treatment Center Intelligence OS</div>
        <div style={s.facility}>Infiniti Group</div>

        {forgot ? (
          <form onSubmit={handleForgot} style={s.form}>
            {forgotSent ? (
              <>
                <div style={s.mustChangeNote}>
                  If that account exists, a reset link has been emailed. It works once and expires in 60 minutes. Check spam if you do not see it within a few minutes.
                </div>
                <button type="button" style={s.btn} onClick={() => { setForgot(false); setForgotSent(false); setError(''); }}>Back to sign in</button>
              </>
            ) : (
              <>
                <div style={s.field}>
                  <label style={s.label}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com" style={s.input} autoFocus required />
                </div>
                {error && <div style={s.error}>{error}</div>}
                <button type="submit" disabled={loading} style={s.btn}>
                  {loading ? 'Sending...' : 'Email me a reset link'}
                </button>
                <button type="button" style={s.forgotLink} onClick={() => { setForgot(false); setError(''); }}>Back to sign in</button>
              </>
            )}
          </form>
        ) : !mustChange ? (
          <form onSubmit={handleLogin} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={s.input}
                autoFocus
                required
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Password</label>
              <div style={s.pwWrap}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{ ...s.input, paddingRight: '46px', width: '100%', boxSizing: 'border-box' }}
                  required
                />
                <button type="button" onClick={() => setShowPw(v => !v)} style={s.eyeBtn}
                  aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            {error && <div style={s.error}>{error}</div>}
            <button type="submit" disabled={loading} style={s.btn}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button type="button" style={s.forgotLink} onClick={() => { setForgot(true); setError(''); }}>Forgot password?</button>
          </form>
        ) : (
          <form onSubmit={handleChangePassword} style={s.form}>
            <div style={s.mustChangeNote}>
              Welcome! You must set a new password before continuing.
            </div>
            <div style={s.field}>
              <label style={s.label}>New Password</label>
              <div style={s.pwWrap}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  style={{ ...s.input, paddingRight: '46px', width: '100%', boxSizing: 'border-box' }}
                  autoFocus
                  required
                />
                <button type="button" onClick={() => setShowPw(v => !v)} style={s.eyeBtn}
                  aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Confirm New Password</label>
              <input
                type={showPw ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                style={s.input}
                required
              />
            </div>
            {error && <div style={s.error}>{error}</div>}
            <button type="submit" disabled={loading} style={s.btn}>
              {loading ? 'Saving...' : 'Set Password & Continue'}
            </button>
          </form>
        )}

        <div style={s.footer}>
          © 2026 Grayscale Technologies · HIPAA Compliant · 42 CFR Part 2
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#0f1623', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  card: { background: 'white', borderRadius: '16px', padding: '48px 40px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  logo: { fontSize: '32px', fontWeight: '800', color: '#336090', letterSpacing: '0.1em', textAlign: 'center', marginBottom: '6px' },
  subtitle: { fontSize: '13px', color: '#7a9cc4', textAlign: 'center', letterSpacing: '0.08em', marginBottom: '4px' },
  facility: { fontSize: '14px', fontWeight: '600', color: '#336090', textAlign: 'center', marginBottom: '32px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: { padding: '11px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border 0.15s' },
  pwWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  eyeBtn: { position: 'absolute', right: '8px', background: 'none', border: 'none', color: '#336090', fontSize: '12px', fontWeight: '700', cursor: 'pointer', padding: '6px', letterSpacing: '0.04em' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', fontSize: '13px' },
  btn: { background: '#336090', color: 'white', border: 'none', padding: '13px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '4px' },
  mustChangeNote: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '12px 14px', borderRadius: '8px', fontSize: '13px', lineHeight: 1.5 },
  forgotLink: { background: 'none', border: 'none', color: '#336090', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: '4px', textAlign: 'center' },
  footer: { marginTop: '28px', fontSize: '11px', color: '#aaa', textAlign: 'center', lineHeight: 1.6 },
};